import React, { useEffect, useState } from 'react';
import {
  AuthErrorMessage,
  Button,
  Dropdown,
  InputField,
  LanguageSwitcher,
  showSignUpSuccessAlert,
  TextAreaField,
} from 'components';
import { BlueLogo } from 'icons';
import { useNavigate } from 'react-router-dom';
import {
  AuthErrorCode,
  countries,
  DEFAULT_PHONE_COUNTRY,
  languages,
  normalizePhoneNumber,
  referralSourceOptions,
  resolveSignUpErrorCode,
} from 'helpers';
import { useTranslation } from 'react-i18next';
import { signUp, SignUpRequest } from 'data/api/userApi';

const MIN_PASSWORD_LENGTH = 8;
const NOTES_MAX_LENGTH = 1000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const Register: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    'en'
  ).split('-')[0];
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountry: DEFAULT_PHONE_COUNTRY,
    phone: '',
    password: '',
    confirmPassword: '',
    language: currentLanguage,
    referralSource: '',
    referralSourceOther: '',
    notes: '',
  });
  const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
  const [errorEmail, setErrorEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setFormData((prev) =>
      prev.language === currentLanguage
        ? prev
        : { ...prev, language: currentLanguage },
    );
  }, [currentLanguage]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === 'language') {
      i18n.changeLanguage(value);
    }
    setErrorCode(null);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'referralSource' && value !== 'other'
        ? { referralSourceOther: '' }
        : {}),
    }));
  };

  const showSignUpError = (code: AuthErrorCode, email: string) => {
    setErrorCode(code);
    setErrorEmail(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitting) {
      return;
    }

    const email = formData.email.trim();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      showSignUpError('nameRequired', email);
      return;
    }

    if (!email) {
      showSignUpError('emailRequired', email);
      return;
    }

    if (!EMAIL_PATTERN.test(email)) {
      showSignUpError('invalidEmailFormat', email);
      return;
    }

    const phone = formData.phone.trim();

    if (!phone) {
      showSignUpError('phoneRequired', email);
      return;
    }

    const normalizedPhone = normalizePhoneNumber(formData.phoneCountry, phone);

    if (!normalizedPhone) {
      showSignUpError('invalidPhoneFormat', email);
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      showSignUpError('passwordRequired', email);
      return;
    }

    if (formData.password.length < MIN_PASSWORD_LENGTH) {
      showSignUpError('passwordTooShort', email);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showSignUpError('passwordsDoNotMatch', email);
      return;
    }

    if (
      !formData.referralSource ||
      (formData.referralSource === 'other' &&
        !formData.referralSourceOther.trim())
    ) {
      showSignUpError('referralSourceRequired', email);
      return;
    }

    setErrorCode(null);

	let req: SignUpRequest = new SignUpRequest();
	req.email = email;
	req.phone = normalizedPhone;
	req.password = formData.password;
	req.confirmPassword = formData.confirmPassword;
	req.firstName = formData.firstName.trim();
	req.lastName = formData.lastName.trim();
	req.language = formData.language;
	req.referralSource =
		formData.referralSource === 'other'
			? formData.referralSourceOther.trim()
			: formData.referralSource;
	req.notes = formData.notes.trim();

	setSubmitting(true);
	try {
		await signUp(req);
		await showSignUpSuccessAlert(email);
		navigate('/login');
	} catch (error) {
		showSignUpError(resolveSignUpErrorCode(error), email);
	} finally {
		setSubmitting(false);
	}
  };

  return (
    <div className="relative flex items-center justify-center min-h-svh bg-[#F5F7F9] flex-col space-y-[24px] px-[16px] py-[32px] sm:py-[64px]">
		<LanguageSwitcher className="absolute top-[16px] right-[16px] sm:top-[24px] sm:right-[24px]" />
		<div>
			<BlueLogo width={122} height={33} />
		</div>
		<div className="bg-white border-[#D5D9DF] border p-[24px] sm:p-[32px] rounded-[24px] w-full text-black flex flex-col items-center max-w-[440px] sm:max-w-[500px] space-y-[17px]">
			<div className="flex flex-col space-y-[4px]">
			<h2 className="text-[24px] leading-[29px] font-[500] text-center text-[#2E435F]">
				{t('register.createAccount')}
			</h2>
			<h2 className="text-[14px] font-[400] text-center text-[#58697F]">
				{t('register.startUsingPayflow')}
			</h2>
			</div>
			<form onSubmit={handleSubmit} className="space-y-[12px] w-full">
			<InputField
				label={t('register.firstName')}
				type="text"
				name="firstName"
				placeholder={t('register.firstName')}
				value={formData.firstName}
				onChange={handleChange}
			/>
			<InputField
				label={t('register.lastName')}
				type="text"
				name="lastName"
				placeholder={t('register.lastName')}
				value={formData.lastName}
				onChange={handleChange}
			/>
			<InputField
				label={t('register.email')}
				type="email"
				name="email"
				placeholder={t('register.email')}
				value={formData.email}
				onChange={handleChange}
			/>
			<div className="flex items-start gap-[12px]">
				<div className="w-[45%] shrink-0">
					<Dropdown
						label={t('register.phoneCountry')}
						name="phoneCountry"
						value={formData.phoneCountry}
						options={countries.map((country) => ({
							label: `${t(country.labelKey)} (+${country.dialCode})`,
							value: country.value,
						}))}
						onChange={handleChange}
						fullWidth
					/>
				</div>
				<div className="flex-1 min-w-0">
					<InputField
						label={t('register.phone')}
						type="tel"
						name="phone"
						placeholder={t('register.phonePlaceholder')}
						value={formData.phone}
						onChange={handleChange}
						fullWidth
					/>
				</div>
			</div>
			<InputField
				label={t('register.password')}
				type="password"
				name="password"
				placeholder={t('register.password')}
				value={formData.password}
				onChange={handleChange}
			/>
			<InputField
				label={t('register.confirmPassword')}
				type="password"
				name="confirmPassword"
				placeholder={t('register.confirmPassword')}
				value={formData.confirmPassword}
				onChange={handleChange}
			/>
			<Dropdown
				label={t('register.language')}
				name="language"
				value={formData.language}
				options={languages.map((lang) => ({
					label: t(lang.labelKey),
					value: lang.value,
				}))}
				onChange={handleChange}
			/>
			<Dropdown
				label={t('register.whereDidYouFindUs')}
				name="referralSource"
				value={formData.referralSource}
				options={[
					{ label: t('referralSourceOptions.select'), value: '' },
					...referralSourceOptions.map((option) => ({
						label: t(option.labelKey),
						value: option.value,
					})),
				]}
				onChange={handleChange}
			/>
			{formData.referralSource === 'other' && (
				<InputField
					label={t('register.whereDidYouFindUsOther')}
					type="text"
					name="referralSourceOther"
					placeholder={t('register.whereDidYouFindUsOtherPlaceholder')}
					value={formData.referralSourceOther}
					onChange={handleChange}
					maxLength={255}
				/>
			)}
			<TextAreaField
				label={t('register.notes')}
				name="notes"
				placeholder={t('register.notesPlaceholder')}
				value={formData.notes}
				onChange={handleChange}
				rows={3}
				maxLength={NOTES_MAX_LENGTH}
			/>
			{errorCode && (
				<AuthErrorMessage code={errorCode} email={errorEmail} />
			)}
			<Button
				type="submit"
				label={submitting ? t('register.registering') : t('register.register')}
				loading={submitting}
				disabled={submitting}
				style={{ width: '100%' }}
			/>
			</form>
		</div>
		<p className="mt-4 text-sm text-[#58697F] font-[700]">
			{t('register.alreadyHaveAccount')}{' '}
			<span
			className="text-[#FF6F61] cursor-pointer hover:underline"
			onClick={() => navigate('/login')}
			>
			{t('register.signIn')}
			</span>
		</p>
    </div>
  );
};
